import { useMDSContext } from '@metservice/use-mds';
import type { IDatability } from '@mono/types/src/weather-widgets';
import React, { useState, useEffect } from 'react';

import { movIntegrationMDS } from '@metservicedev/platforms-services-interface';
import type { Capability } from '@mono/types/src/mds';
import { usePlatStageContext } from '@mono/use-plat-stage';
import { capabilitiesByDataTypes } from '../../utils/capabilitiesByDataTypes';
import CapabilitySelector from '../CapabilitySelector';
import UnitSelector from '../UnitSelector';
import type { MinimalDataset } from './DatasetFormGroup';
import Select from './SelectOptionFormGroup';

interface CapabilityFormGroupProps extends Pick<React.ComponentProps<typeof UnitSelector>, 'unitLabel' | 'onUnitLabelChange'> {
    unit?: string;
    onCapabilityChange(id: string): void;
    onUnitChange?(unit: string): void;
    dataType?: string[];
    vectorNormalization?: 'magnitude' | 'direction';
    onVectorNormalizationChange?(value: string): void;
    datability: IDatability;
    loadedDatasetIds?: string[];
    passedDownDatasets?: MinimalDataset[];
}

const VECTOR_NORMALIZATION_VALUES = ['magnitude', 'direction'];

const CapabilityFormGroup: React.FC<CapabilityFormGroupProps> = ({
    unit,
    unitLabel,
    onCapabilityChange,
    onUnitChange,
    onUnitLabelChange,
    dataType = null,
    vectorNormalization,
    onVectorNormalizationChange,
    datability,
    loadedDatasetIds,
    passedDownDatasets,
}) => {
    const { capabilities } = useMDSContext();
    const { capabilityId, datasetId } = datability;
    const [validCapabilities, setValidCapabilities] = useState<Capability[]>(capabilitiesByDataTypes(capabilities, dataType ?? []));
    const [isLoadingCapabilities, setIsLoadingCapabilities] = useState(false);
    const capability = validCapabilities.find(({ id }) => id === capabilityId);
    const enforceDirection = capability?.data_type === 'vector' && !!vectorNormalization;
    const { stage } = usePlatStageContext();

    useEffect(() => {
        if (!loadedDatasetIds) {
            return;
        }
        const datasetIds =
            datasetId === null
                ? passedDownDatasets && passedDownDatasets.length > 0
                    ? passedDownDatasets.map((dataset) => dataset.id)
                    : loadedDatasetIds
                : datasetId
                  ? [datasetId]
                  : loadedDatasetIds;
        setIsLoadingCapabilities(true);
        movIntegrationMDS.http
            .getCapabilitiesByDatasetIds({
                datasetIds,
                metjwt: JSON.parse(localStorage.getItem('okta-token-storage') || '{}')?.accessToken?.accessToken,
                stage,
                headers: { 'X-Current-Org': localStorage.getItem('mip-organisation-id') },
            })
            .then((response) => {
                setValidCapabilities(capabilities.filter((c) => response.data.includes(c.id)));
            })
            .finally(() => {
                setIsLoadingCapabilities(false);
            });
    }, [loadedDatasetIds, datasetId]);

    return (
        <>
            <div style={{ gridColumn: 1 }}>
                <strong>Capability:</strong>
            </div>
            <div style={{ gridColumn: '2/6' }}>
                <CapabilitySelector
                    loading={isLoadingCapabilities}
                    value={capabilityId}
                    capabilities={validCapabilities}
                    onChange={onCapabilityChange}
                    dataType={dataType}
                />
            </div>
            {enforceDirection && (
                <Select
                    label='Vector normalization'
                    defaultValue='magnitude'
                    values={VECTOR_NORMALIZATION_VALUES}
                    value={vectorNormalization}
                    onChange={onVectorNormalizationChange}
                />
            )}
            {onUnitChange && (
                <>
                    <div style={{ gridColumn: 1 }}>
                        <strong>Unit:</strong>
                    </div>
                    <div style={{ gridColumn: '2/6' }}>
                        <UnitSelector
                            value={unit}
                            onChange={onUnitChange}
                            unitLabel={unitLabel}
                            onUnitLabelChange={onUnitLabelChange}
                            capabilityId={enforceDirection && vectorNormalization === 'direction' ? 'wnd_dir_from_100m' : capabilityId}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default React.memo(CapabilityFormGroup);
